import { Dispatch, SetStateAction, useCallback, useRef, useState } from "react";
import { recalculateSaleReport } from "../../../services";
import { Link } from "react-router-dom";
import { SalesReportSummary } from "../../../types/sale-reports.type";
import { formatThaiFullDate } from "../../../utils/date";
import { useUserRole } from "../../../utils/hooks/useUserRole";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ImageSearchIcon from "@mui/icons-material/ImageSearch";
import SyncIcon from "@mui/icons-material/Sync";
import Snackbar from "@mui/material/Snackbar";
import { COMPACT_QUERY } from "../../../utils/hooks/useCompact";

const compact = "@media " + COMPACT_QUERY;

interface DetailsHeaderProps {
  reportId?: string;
  reportSummary: SalesReportSummary;
  isReport: boolean;
  setTriggerFetch: Dispatch<SetStateAction<number>>;
  setOpenModalImage: Dispatch<SetStateAction<boolean>>;
}

const LIMIT = 3000;

export default function DetailsHeader({
  reportId,
  reportSummary,
  isReport,
  setTriggerFetch,
  setOpenModalImage,
}: DetailsHeaderProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const [syncing, setSyncing] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);

  const syncTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onRecalculateSaleReport = useCallback(async () => {
    setSyncing(true);
    try {
      if (reportId) {
        await recalculateSaleReport(reportId);
      }
      setSnackbarOpen(true);
      setTriggerFetch((prev) => prev + 1);
    } finally {
      syncTimeoutRef.current = setTimeout(() => {
        setSyncing(false);
      }, LIMIT);
    }
  }, [reportId, setTriggerFetch]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          marginBottom: "16px",
          paddingBottom: "16px",
          justifyContent: "space-between",
          borderBottom: (theme) => `1px solid ${theme.palette.draft.main}`,
          [compact]: {
            flexWrap: "wrap",
            gap: 1,
            marginBottom: "12px",
            paddingBottom: "12px",
          },
        }}
      >
        <Box sx={{ display: "flex" }}>
          <Link
            to="/report/sale"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <ArrowBackIosNewIcon />
          </Link>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              marginLeft: "6px",
              position: "relative",
              bottom: "5px",
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 500 }}>
              รายงานการขาย (Sale Daily Report)
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                [compact]: { gap: 1.5, flexWrap: "wrap" },
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  สาขา
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: (theme) => theme.palette.gray.main }}
                >
                  {reportSummary.branch_code}
                </Typography>
              </Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Typography variant="caption" sx={{ fontWeight: 500 }}>
                  วันที่สร้าง
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: (theme) => theme.palette.gray.main }}
                >
                  {formatThaiFullDate(reportSummary.created_at)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "16px",
          }}
        >
          {isReport &&
            reportSummary.additional_pictures &&
            reportSummary.additional_pictures.length !== 0 && (
              <Button
                variant="outlined"
                startIcon={<ImageSearchIcon />}
                onClick={() => setOpenModalImage(true)}
              >
                ดูรูปภาพเพิ่มเติม
              </Button>
            )}
          {!isHeadOfficeAccounting && (
            <Button
              variant="contained"
              disabled={syncing}
              startIcon={<SyncIcon />}
              onClick={onRecalculateSaleReport}
            >
              อัพเดทข้อมูล
            </Button>
          )}
        </Box>
      </Box>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={1500}
        onClose={() => setSnackbarOpen(false)}
        message="อัพเดทข้อมูลข้อมูลสำเร็จ"
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      />
    </>
  );
}
